import * as React from 'react'
import { Badge, Button, Card, CardContent, CardHeader } from '@/shared/ui'
import { RefreshCcw, ShieldCheck, ShieldX } from 'lucide-react'
import { useInstallabilityDiagnostics } from '@/modules/pwa/hooks/useInstallabilityDiagnostics'
import { usePwaInstall } from '@/modules/pwa'

function YesNoBadge({ ok }: { ok: boolean }) {
  return (
    <Badge variant="soft" color={ok ? 'success' : 'error'}>
      {ok ? (
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="h-3.5 w-3.5" />
          OK
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          <ShieldX className="h-3.5 w-3.5" />
          Nao
        </span>
      )}
    </Badge>
  )
}

export default function InstallabilityDiagnosticsCard() {
  const { loading, diag, refresh } = useInstallabilityDiagnostics()
  const { canInstall, hasPrompt, installed, isIosSafari } = usePwaInstall()

  const preferred = React.useMemo(() => {
    if (!diag.preferredManifestHref) return null
    return diag.manifests.find((m) => m.href === diag.preferredManifestHref) || null
  }, [diag.manifests, diag.preferredManifestHref])

  const manifestOk = !!preferred?.ok

  const issues = React.useMemo(() => {
    const list: string[] = []
    if (!diag.secureContext) list.push('Contexto inseguro: use HTTPS (localhost e permitido).')
    if (!diag.serviceWorkerSupported) list.push('Service Worker nao suportado neste navegador.')
    if (diag.serviceWorkerSupported && !diag.swRegistered) list.push('Service Worker nao registrado (verifique /sw.js e console).')
    if (diag.serviceWorkerSupported && diag.swRegistered && !diag.swController) list.push('SW registrado, mas ainda nao esta controlando a pagina (tente recarregar).')
    if (!diag.manifests.length) list.push('Nenhum manifest foi detectado no <head>.')
    if (diag.manifests.length && !manifestOk) list.push('Manifest detectado, mas falhou ao carregar/parsear (pode estar retornando HTML por rewrite).')
    if (!installed && !isIosSafari && diag.secureContext && diag.swRegistered && diag.swController && manifestOk && !canInstall) {
      list.push('O Chrome/Edge pode ainda nao ter liberado o prompt (heuristica de engajamento). Teste apos navegar/usar por alguns minutos ou use a flag chrome://flags/#bypass-app-banner-engagement-checks (dev).')
    }
    return list
  }, [canInstall, diag.manifests.length, diag.secureContext, diag.serviceWorkerSupported, diag.swController, diag.swRegistered, installed, isIosSafari, manifestOk])

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div className="text-sm font-medium">Diagnostico de instalacao (Chrome/Edge)</div>
        <Button variant="outline" className="h-9" onClick={refresh} disabled={loading}>
          <RefreshCcw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-foreground/5 px-3 py-2">
            <div className="text-sm">Contexto seguro</div>
            <YesNoBadge ok={diag.secureContext} />
          </div>
          <div className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-foreground/5 px-3 py-2">
            <div className="text-sm">Service Worker suportado</div>
            <YesNoBadge ok={diag.serviceWorkerSupported} />
          </div>
          <div className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-foreground/5 px-3 py-2">
            <div className="text-sm">SW registrado</div>
            <YesNoBadge ok={diag.swRegistered} />
          </div>
          <div className="flex items-center justify-between gap-2 rounded-md border border-border/40 bg-foreground/5 px-3 py-2">
            <div className="text-sm">SW controlando</div>
            <YesNoBadge ok={diag.swController} />
          </div>
        </div>

        <div className="text-sm text-foreground/80">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground/70">Prompt capturado:</span>
            <Badge variant="soft" color={hasPrompt ? 'success' : 'warning'}>{hasPrompt ? 'Sim' : 'Nao'}</Badge>
            <span className="text-foreground/70">Pode instalar agora:</span>
            <Badge variant="soft" color={canInstall ? 'success' : 'warning'}>{canInstall ? 'Sim' : 'Nao'}</Badge>
            <span className="text-foreground/70">Instalado:</span>
            <Badge variant="soft" color={installed ? 'success' : 'default'}>{installed ? 'Sim' : 'Nao'}</Badge>
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Manifest</div>
          {!diag.manifests.length && <div className="text-xs text-foreground/70">Nenhum link rel=&quot;manifest&quot; encontrado.</div>}
          {!!diag.manifests.length && (
            <div className="space-y-1 text-xs text-foreground/70">
              {diag.manifests.map((m) => (
                <div key={m.href} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/40 px-3 py-2">
                  <div className="min-w-0 truncate">
                    <span className="font-mono">{m.href}</span>
                    {m.contentType && <span className="ml-2 text-foreground/50">({m.contentType})</span>}
                  </div>
                  <Badge variant="soft" color={m.ok ? 'success' : 'error'}>
                    {m.ok ? 'OK' : `Falha${m.status ? ` (${m.status})` : ''}`}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          {diag.preferredManifestHref && (
            <div className="text-xs text-foreground/70">
              Preferido: <span className="font-mono">{diag.preferredManifestHref}</span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="rounded-md border border-border/40 px-3 py-2 text-xs text-foreground/70">
              <div className="text-foreground/80">start_url</div>
              <div className="font-mono">{diag.manifestStartUrl || '-'}</div>
            </div>
            <div className="rounded-md border border-border/40 px-3 py-2 text-xs text-foreground/70">
              <div className="text-foreground/80">scope</div>
              <div className="font-mono">{diag.manifestScope || '-'}</div>
            </div>
            <div className="rounded-md border border-border/40 px-3 py-2 text-xs text-foreground/70">
              <div className="text-foreground/80">display</div>
              <div className="font-mono">{diag.manifestDisplay || '-'}</div>
            </div>
            <div className="rounded-md border border-border/40 px-3 py-2 text-xs text-foreground/70">
              <div className="text-foreground/80">icons</div>
              <div className="font-mono">{typeof diag.manifestIconsCount === 'number' ? String(diag.manifestIconsCount) : '-'}</div>
            </div>
          </div>
        </div>

        {!!diag.swDiag && (
          <div className="rounded-md border border-border/40 bg-foreground/5 px-3 py-2 text-xs text-foreground/70">
            <span className="text-foreground/80">SW diag:</span> <span className="font-mono">{diag.swDiag}</span>
          </div>
        )}

        {!!issues.length && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
            <div className="font-medium">Possiveis causas</div>
            <ul className="mt-1 list-disc pl-5">
              {issues.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

